import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useResources } from '../hooks/useResources';
import { FormattedMessage } from 'react-intl';
import { IconCalendar } from '../components/icons';
import { PageHeader } from '../components/PageHeader';
import { useInteractionTimeout } from '../hooks/useInteractionTimeout';
import { useConfig } from '../providers/ConfigProvider';
import { useNavigateToDefaultView } from '../hooks/useNavigateToDefaultView';
import { DateTime } from 'luxon';
import { CalendarDay } from '../components/Booking/CalendarDay';
import { ResourceIcon } from '../components/Booking/ResourceIcon';
import { BookingModal } from '../components/Booking/BookingModal';
import { useResourceSchema } from '../hooks/useResourceSchema';
import { useUserBookings, useResourceBookings } from '../hooks/useBookings';
import {
  mapAndFillApiSlots,
  groupApiSlotsByDay,
} from '../utils/slotGeneration';
import { BookingResource, BookingSlot } from '../types/booking';
import { useAuth } from '../hooks/useAuth';
import { SLOT_STYLES } from '../components/Booking/slotStyles';
import { HeaderContent } from '../components/Booking/HeaderContent';

interface Props {
  portrait?: boolean;
}

export const CalendarView: FC<Props> = ({ portrait }) => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const { data: resources = [] } = useResources();
  const resource = useMemo(
    () => resources.find((r) => r.id === resourceId),
    [resources, resourceId],
  );

  if (!resource) {
    return null;
  }

  return <Content resource={resource} portrait={portrait} />;
};

const Content: FC<{ resource: BookingResource; portrait?: boolean }> = ({
  resource,
  portrait,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { interactionTimeout, nfcKeyTimeout } = useConfig();
  const { logout } = useAuth();
  const navigateToDefaultView = useNavigateToDefaultView([ref]);
  useInteractionTimeout(nfcKeyTimeout, logout);
  useInteractionTimeout(interactionTimeout, navigateToDefaultView);

  const today = DateTime.now().startOf('day');
  const startOfWeek = today.startOf('week');
  const startDate = startOfWeek;
  const endDate = startDate.plus({ weeks: 4 });

  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedSlot, setSelectedSlot] = useState<BookingSlot | null>(null);

  const {
    data: iloqSchema,
    isLoading: isLoadingSchema,
    error: schemaError,
  } = useResourceSchema({
    resourceId: resource.id,
  });

  const { data: existingBookings = [] } = useResourceBookings(resource.id);

  const { data: userBookings = [] } = useUserBookings([resource]);

  const resourceUserBookings = useMemo(
    () => userBookings.filter((booking) => booking.resourceId === resource.id),
    [userBookings, resource.id],
  );

  const slotsByDay = useMemo(() => {
    const mappedSlots = mapAndFillApiSlots(
      iloqSchema?.days,
      existingBookings,
      resourceUserBookings,
    );
    return groupApiSlotsByDay(mappedSlots);
  }, [iloqSchema, existingBookings, resourceUserBookings]);

  // Generate calendar grid data
  const calendarWeeks = useMemo(() => {
    const weeks: DateTime[][] = [];

    let maxDate = endDate;
    if (iloqSchema?.days && iloqSchema.days.length > 0) {
      const lastApiDateStr = iloqSchema.days[iloqSchema.days.length - 1].date;
      const lastApiDate = DateTime.fromISO(lastApiDateStr);
      if (lastApiDate.isValid) {
        maxDate = lastApiDate;
      }
    }
    const validMaxDate = maxDate.isValid ? maxDate : endDate;
    const diffInWeeks = Math.ceil(
      validMaxDate.diff(startOfWeek, 'weeks').weeks,
    );
    const weeksInAdvance = Math.max(1, diffInWeeks);

    for (let weekIndex = 0; weekIndex < weeksInAdvance; weekIndex++) {
      const weekDays: DateTime[] = [];
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const date = startOfWeek.plus({ days: weekIndex * 7 + dayIndex });
        weekDays.push(date);
      }
      weeks.push(weekDays);
    }

    return weeks;
  }, [endDate, iloqSchema?.days, startOfWeek]);

  const handleSlotSelect = useCallback((slot: BookingSlot) => {
    setSelectedSlot(slot);
    const slotStartDate = DateTime.fromISO(slot.start);
    if (slotStartDate.isValid) {
      setSelectedDate(slotStartDate);
    }
  }, []);

  if (!resource) {
    return (
      <div className="size-full flex items-center justify-center">
        <p className="text-gray-500">
          <FormattedMessage defaultMessage="Resursen kunde inte hittas" />
        </p>
      </div>
    );
  }

  if (isLoadingSchema) {
    return (
      <div className="size-full flex items-center justify-center">
        <p className="text-gray-500">
          <FormattedMessage defaultMessage="Laddar..." />
        </p>
      </div>
    );
  }

  if (schemaError) {
    return (
      <div className="size-full flex items-center justify-center">
        <p className="text-red-500">
          <FormattedMessage defaultMessage="Kunde inte ladda bokningsschema" />
        </p>
      </div>
    );
  }

  return (
    <div ref={ref} className="size-full">
      <PageHeader
        icon={<IconCalendar />}
        title={<FormattedMessage defaultMessage="Bokning" />}
        content={<HeaderContent />}
      />

      <main
        className="flex gap-8 pl-11 pr-12 w-full"
        style={{ height: `calc(100% - ${portrait ? 90 : 140}px)` }}
      >
        <div className="gap-2 flex flex-col w-full h-full overflow-auto px-1 pb-8">
          <div className="flex text-gray-900 items-center gap-3">
            <ResourceIcon type={resource.type} className="size-10" />
            <h2 className="font-medium">{resource.name}</h2>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarWeeks.map((week) =>
              week.map((date) => {
                const dateKey = date.toISODate();
                const daySlots = dateKey ? slotsByDay.get(dateKey) || [] : [];
                const isSelected = dateKey === selectedDate.toISODate();

                return (
                  <CalendarDay
                    key={dateKey}
                    date={date}
                    slots={daySlots}
                    selected={isSelected}
                    onDaySelect={() => {
                      const dateToSelect = DateTime.fromISO(
                        date.toISODate() || '',
                      );
                      if (dateToSelect.isValid) {
                        setSelectedDate(dateToSelect);
                      }
                    }}
                    viewContext="calendar"
                  />
                );
              }),
            )}
          </div>
        </div>

        <div className="w-80 min-w-80 max-w-80 flex flex-col overflow-auto pb-8">
          <h3 className="flex font-medium text-gray-900">
            {selectedDate.toFormat('cccc d MMMM')}
          </h3>

          <div className="flex flex-col gap-2">
            <SideBarContent
              selectedDate={selectedDate}
              slotsByDay={slotsByDay}
              isLoading={isLoadingSchema}
              onSlotSelect={handleSlotSelect}
            />
          </div>
        </div>

        {selectedSlot && (
          <BookingModal
            mode={selectedSlot.status === 'userBooked' ? 'cancel' : 'book'}
            resource={resource}
            timeSlot={selectedSlot}
            onClose={() => setSelectedSlot(null)}
          />
        )}
      </main>
    </div>
  );
};

interface SideBarContentProps {
  slotsByDay: Map<string, BookingSlot[]>;
  selectedDate: DateTime;
  isLoading: boolean;
  onSlotSelect: (slot: BookingSlot) => void;
}

const SideBarContent: FC<SideBarContentProps> = ({
  slotsByDay,
  selectedDate,
  isLoading,
  onSlotSelect,
}) => {
  if (isLoading) {
    return (
      <>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-lg" />
        ))}
      </>
    );
  }

  const slots = slotsByDay.get(selectedDate.toISODate() || '');

  if (!slots) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FormattedMessage defaultMessage="Inga tider tillgängliga denna dag" />
      </div>
    );
  }

  return (
    <>
      {slots.map((slot) => {
        const startTime = DateTime.fromISO(slot.start).setZone('local');
        const endTime = DateTime.fromISO(slot.end).setZone('local');
        const style = SLOT_STYLES[slot.status];

        return (
          <button
            key={slot.id}
            onClick={() => style.cursor === 'pointer' && onSlotSelect(slot)}
            className="w-full p-3 rounded-lg text-left"
            style={{ background: style.color, color: style.textColor }}
            disabled={style.cursor !== 'pointer'}
          >
            <p className="font-medium">
              {startTime.toFormat('HH:mm')} - {endTime.toFormat('HH:mm')}
            </p>
            <p className="text-sm">{style.label}</p>
          </button>
        );
      })}
    </>
  );
};
